import React, { useCallback, useMemo, MouseEvent } from 'react';
import useIsMobile from 'shared/hooks/useIsMobile';
import partial from 'lodash-es/partial';
import Tooltip from 'shared/components/Tooltip';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import { ChannelFilterSelectionFragment, FilterSelectionType } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { IChannelListFilterSelection } from 'pages/Conversations/interfaces/IChannelListFilterSelection';
import ChannelListFilterPopoverHeader from './ChannelListFilterPopoverHeader';
import { Box, Flex, HStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';
import { Icon } from '@televet/kibble-ui/build/components/Icon';

interface IChannelListFilterSelectProps {
  channelFilterSelections: ChannelFilterSelectionFragment[] | undefined;
  currentChannelFilterSelection: IChannelListFilterSelection;
  isQueryLoading: boolean;
  initialFocusRef: React.MutableRefObject<HTMLDivElement | null>;
  onClearFilterClick: (source: string) => void;
  onEditFilterClick: (channelFilterSelection: ChannelFilterSelectionFragment) => void;
  onSelectFilterClick: (channelFilterSelection: ChannelFilterSelectionFragment) => void;
  onViewFilterEdit: () => void;
  onDeleteFilterClick: (channelFilterSelection: ChannelFilterSelectionFragment) => void;
  onClosePopover: () => void;
}

const ChannelListFilterSelect = ({
  channelFilterSelections,
  currentChannelFilterSelection,
  isQueryLoading,
  initialFocusRef,
  onClearFilterClick,
  onEditFilterClick,
  onSelectFilterClick,
  onViewFilterEdit,
  onDeleteFilterClick,
  onClosePopover,
}: IChannelListFilterSelectProps): JSX.Element => {
  const { clinicUser } = useClinicUser();
  const { isMobile } = useIsMobile();
  const { trackEvent } = useTracking();

  const handleClearFilterClick = useCallback(
    (source: string, event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClearFilterClick(source);
    },
    [onClearFilterClick],
  );

  const handleEditFilterClick = useCallback(
    (
      channelFilterSelection: ChannelFilterSelectionFragment,
      isFilterOwnedByCurrentUser: boolean,
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      event.stopPropagation();

      const eventName = isFilterOwnedByCurrentUser
        ? ConversationEvents.FiltersEditViewed
        : ConversationEvents.FiltersViewed;
      trackEvent(eventName);
      onEditFilterClick(channelFilterSelection);
    },
    [onEditFilterClick, trackEvent],
  );

  const handleSelectFilterClick = useCallback(
    (channelFilterSelection: ChannelFilterSelectionFragment) => {
      if (currentChannelFilterSelection.id && channelFilterSelection.id === currentChannelFilterSelection.id) {
        onClearFilterClick('filter-popover-select-row-click');
      } else {
        trackEvent(ConversationEvents.FilterSelected);
        onSelectFilterClick(channelFilterSelection);
      }
    },
    [currentChannelFilterSelection.id, onClearFilterClick, onSelectFilterClick, trackEvent],
  );

  const handleDeleteFilterClick = useCallback(
    (channelFilterSelection: ChannelFilterSelectionFragment, event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDeleteFilterClick(channelFilterSelection);
    },
    [onDeleteFilterClick],
  );

  const handleNewFilterClick = useCallback(() => {
    onViewFilterEdit();
    // This timeout is necessary to both clear the filter and navigate to the
    // edit screen at the same time. I can't pinpoint why removing it prevents
    // the desired behavior, but we should investigate and fix this at some point.
    setTimeout(() => {
      onClearFilterClick('saved-filters-footer-new-filter-button');
    }, 0);
  }, [onClearFilterClick, onViewFilterEdit]);

  const sortedChannelFilterSelections = useMemo(() => {
    return channelFilterSelections?.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [channelFilterSelections]);

  return (
    <>
      <Box flex="0 0 auto" p={3} borderBottom="1px" borderBottomColor="border.default">
        <HStack justify="space-between" pl={1}>
          <ChannelListFilterPopoverHeader
            iconFirst={true}
            onHandleSaveFilterButtonClick={onViewFilterEdit}
            isClearButtonDisabled={!currentChannelFilterSelection.id}
            onClearButtonClick={partial(handleClearFilterClick, 'filter-popover-select-header-clear-button')}
          />
        </HStack>
      </Box>
      <Box
        data-testid="channel-list-popover-body-filter-select"
        flex="1 1 auto"
        overflow="auto"
        maxH="400px"
        overflowY="auto"
      >
        {isQueryLoading && !channelFilterSelections ? (
          <Box p={4}>
            <LoadingIndicator />
          </Box>
        ) : !isQueryLoading && !channelFilterSelections?.length ? (
          <Box p={4}>
            <Text fontSize="sm">You and your team have no saved conversation filters.</Text>
          </Box>
        ) : (
          sortedChannelFilterSelections?.map((channelFilterSelection, index) => {
            const { id, name, filterSelectionType, userChannelFilterSelections } = channelFilterSelection;
            const filterOwner = userChannelFilterSelections[0];
            const isFilterOwnedByCurrentUser = filterOwner.user.id === clinicUser?.id;
            // TODO: Convert this to @client computed field in Apollo cache
            const filterOwnerName = isFilterOwnedByCurrentUser
              ? 'Me'
              : [filterOwner.user.firstName, filterOwner.user.lastName].join(' ').trim();
            const isShared = filterSelectionType === FilterSelectionType.PublicReadonly;
            const isCurrentFilter = currentChannelFilterSelection.id === id;
            const isLastRow = index === sortedChannelFilterSelections.length - 1;
            return (
              <Flex
                key={id}
                align="center"
                justify="space-between"
                h="48px"
                px={2}
                cursor="pointer"
                role="group"
                tabIndex={0}
                overflow="hidden"
                borderBottom="1px"
                borderBottomColor={isLastRow ? 'transparent' : 'border.default'}
                _hover={{ bg: 'background.alpha.hover' }}
                _focus={{ bg: 'background.alpha.pressed' }}
                ref={index === 0 ? initialFocusRef : null}
                onClick={partial(handleSelectFilterClick, channelFilterSelection)}
              >
                <Flex align="center" flex="1 1 auto" overflow="hidden">
                  <Flex
                    width="28px"
                    flex=" 0 0 auto"
                    align="center"
                    justify="flex-end"
                    onClick={(e): void => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleSelectFilterClick(channelFilterSelection);
                    }}
                  >
                    <Checkbox isChecked={isCurrentFilter} formControlStyle={{ width: 'auto' }} isReadOnly />
                  </Flex>
                  <Text
                    data-testid={`channel-list-popover-body-saved-filter-${name.split(' ').join('-')}`}
                    pl="4"
                    isTruncated={true}
                  >
                    {name}
                  </Text>
                  {isShared && !!filterOwner && (
                    <Tooltip
                      content={`This filter is shared with your team${
                        filterOwner.user.id !== clinicUser?.id ? ` by ${filterOwnerName}` : ''
                      }.`}
                    >
                      <Flex align="center" ml="4" flex="0 0 auto">
                        <Icon name="team" size="md" variant="subtle" />
                        <Text ml="1" size="sm" fontWeight="bold">
                          {filterOwnerName}
                        </Text>
                      </Flex>
                    </Tooltip>
                  )}
                </Flex>
                <HStack
                  alignItems="center"
                  display={isMobile ? 'visible' : 'none'}
                  _groupHover={{ display: 'flex' }}
                  _groupFocus={{ display: 'flex' }}
                  spacing="2"
                >
                  {isFilterOwnedByCurrentUser && (
                    <Button
                      iconName="trashcan"
                      variant="ghostNeutral"
                      aria-label="Delete filter"
                      size="sm"
                      onClick={partial(handleDeleteFilterClick, channelFilterSelection)}
                    />
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={partial(handleEditFilterClick, channelFilterSelection, isFilterOwnedByCurrentUser)}
                  >
                    {isFilterOwnedByCurrentUser ? 'Edit' : 'View'}
                  </Button>
                </HStack>
              </Flex>
            );
          })
        )}
      </Box>
      <Box borderTop="1px" borderTopColor="border.default" flex="0 0 auto" px={4} py={3}>
        <Flex align="center" justify="flex-end">
          <HStack>
            <Button
              data-testid={`channel-list-filter-popover-footer-New-Filter`}
              variant="secondary"
              size="sm"
              iconName="plus"
              iconPosition="left"
              onClick={handleNewFilterClick}
            >
              New Filter
            </Button>
            <Button
              data-testid="channel-list-filter-popover-footer-Done"
              variant="primary"
              size="sm"
              onClick={onClosePopover}
            >
              Done
            </Button>
          </HStack>
        </Flex>
      </Box>
    </>
  );
};

export default ChannelListFilterSelect;
